import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, PageHead, Section, Sq, Tabs, Time } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import {
  EVIDENCE_LABEL,
  money,
  QUOTE_STATUS_LABEL,
  type Comparisons,
  type EvidenceType,
  type ExchangeRate,
  type PricingSettings,
  type QuotationSummary,
} from '../../lib/pricingTypes';
import { useApi, useSession } from '../../lib/session';
import './pricing.css';

const TABS = [
  { id: 'quotations', label: 'Quotations' },
  { id: 'comparisons', label: 'Comparisons' },
  { id: 'settings', label: 'Settings' },
] as const;
type TabId = (typeof TABS)[number]['id'];

export function statusTone(s: string) {
  return s === 'approved' ? 'ok' : s === 'needs_review' ? 'warn' : s === 'extracting' ? 'live' : s === 'failed' || s === 'malformed' || s === 'password_protected' ? 'bad' : 'idle';
}

export function QuoteStatusMark({ status }: { status: QuotationSummary['status'] }) {
  const tone = statusTone(status);
  return (
    <span className={`status tone-${tone}`}>
      <Sq kind={status === 'extracting' ? 'outline' : 'filled'} tone={tone} /> {QUOTE_STATUS_LABEL[status]}
    </span>
  );
}

export function Pricing() {
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((t) => t.id === sp.get('tab'))?.id ?? 'quotations') as TabId;
  return (
    <div className="page">
      <PageHead title="Pricing" meta="Quotations · reviewed evidence only · confidential" />
      <p className="lede pricing-lede">
        Upload a PDF quotation: the worker reads its text (OCR for scanned pages), proposes fields, lines and conditions with the page they came from, and you
        review every value. Only approved quotations count in comparisons.
      </p>
      <Tabs tabs={[...TABS]} value={tab} onChange={(t) => setSp(t === 'quotations' ? {} : { tab: t }, { replace: true })} />
      {tab === 'quotations' && <QuotationList />}
      {tab === 'comparisons' && <ComparisonsTab />}
      {tab === 'settings' && <PricingSettingsTab />}
    </div>
  );
}

function QuotationList() {
  const list = useApi<{ items: QuotationSummary[] }>('/api/quotations');
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [evidence, setEvidence] = useState<EvidenceType>('supplier_quotation');
  const [drag, setDrag] = useState(false);

  // Poll while anything is still being extracted.
  const extracting = list.data?.items.some((q) => q.status === 'extracting');
  useEffect(() => {
    if (!extracting) return;
    const t = window.setInterval(list.reload, 2000);
    return () => window.clearInterval(t);
  }, [extracting, list.reload]);

  async function send(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError('');
    setNotice('');
    const form = new FormData();
    form.append('file', file);
    form.append('evidence_type', evidence);
    try {
      const r = await api<{ quotation: QuotationSummary; duplicate: boolean; message: string | null }>('/api/quotations', { form });
      if (r.duplicate) {
        setNotice(r.message ?? 'This file was already uploaded.');
        list.reload();
      } else if (r.quotation.status === 'extracting') {
        navigate(`/pricing/quotations/${r.quotation.id}`);
      } else {
        setNotice(`${file.name}: ${QUOTE_STATUS_LABEL[r.quotation.status]}. ${r.quotation.status_detail ?? ''}`);
        list.reload();
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Section title="Upload a quotation">
        <div
          className={`dropzone${drag ? ' over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            void send(e.dataTransfer.files[0]);
          }}
        >
          <label className="field compact-field">
            <span>Evidence type</span>
            <select value={evidence} onChange={(e) => setEvidence(e.target.value as EvidenceType)}>
              {Object.entries(EVIDENCE_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className={`btn primary file-btn${busy ? ' disabled' : ''}`}>
            {busy ? 'Uploading…' : 'Choose a PDF'}
            <input type="file" accept="application/pdf,.pdf" aria-label="Upload a quotation PDF" disabled={busy} onChange={(e) => void send(e.target.files?.[0] ?? undefined)} />
          </label>
          <span className="muted small">or drop it here · PDF up to 20 MB · stored once, never modified</span>
        </div>
        <ErrorMsg>{error}</ErrorMsg>
        {notice && (
          <p className="msg" role="status">
            {notice}
          </p>
        )}
      </Section>

      <Section title="Quotations" aside={list.data && <span className="muted small">{list.data.items.length} uploaded</span>}>
        {list.loading && !list.data && <Loading what="Loading quotations" />}
        <ErrorMsg>{list.error}</ErrorMsg>
        {list.data && list.data.items.length === 0 && <Empty>No quotation uploaded yet.</Empty>}
        {list.data && list.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Supplier</th>
                  <th>Date</th>
                  <th className="num">Total</th>
                  <th>To review</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((q) => (
                  <tr key={q.id}>
                    <td className="clip">
                      <Link className="studio-name" to={`/pricing/quotations/${q.id}`}>
                        {q.filename}
                      </Link>
                      <div className="muted small">
                        {EVIDENCE_LABEL[q.evidence_type]}
                        {q.page_count ? ` · ${q.page_count} p.` : ''}
                      </div>
                    </td>
                    <td>
                      <QuoteStatusMark status={q.status} />
                      {q.status_detail && q.status !== 'needs_review' && <div className="muted small clip">{q.status_detail}</div>}
                    </td>
                    <td>{q.supplier ?? <span className="unknown">unknown</span>}</td>
                    <td className="num">{q.issue_date ?? <span className="unknown">unknown</span>}</td>
                    <td className="num">{q.total ? money(q.total, q.currency) : <span className="unknown">unknown</span>}</td>
                    <td className="num">{q.pending?.total ? <Badge tone="warn">{q.pending.total}</Badge> : <span className="muted">—</span>}</td>
                    <td>
                      <Time iso={q.created_at} relative />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}

function ComparisonsTab() {
  const [category, setCategory] = useState('');
  const [withOptional, setWithOptional] = useState(false);
  const qs = new URLSearchParams();
  if (category) qs.set('category', category);
  if (withOptional) qs.set('include_optional', 'true');
  const data = useApi<Comparisons>(`/api/pricing/comparisons?${qs.toString()}`);

  return (
    <>
      <p className="msg warn">
        Reviewed evidence only: lines you accepted or corrected on <strong>approved</strong> quotations. A quotation is evidence for its own scope, date and context,
        not a studio’s tariff. Ranges are shown with their sample counts; no average is computed. Bundles stay bundles: no unit price is derived from them.
      </p>
      <div className="filters">
        <label className="select-filter">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
            <option value="">All</option>
            {data.data?.categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={withOptional} onChange={(e) => setWithOptional(e.target.checked)} /> include optional items
        </label>
      </div>
      {data.loading && !data.data && <Loading what="Loading comparisons" />}
      <ErrorMsg>{data.error}</ErrorMsg>
      {data.data && data.data.groups.length === 0 && <Empty>No reviewed evidence yet. Approve a quotation to see its lines here.</Empty>}
      {data.data?.groups.map((g) => (
        <Section
          key={`${g.category}-${g.currency}`}
          title={
            <>
              {g.category} <span className="muted">· {g.currency}</span>
            </>
          }
          aside={
            <span className="small muted">
              {g.sample_count} {g.sample_count === 1 ? 'line' : 'lines'} · {g.quotation_count} {g.quotation_count === 1 ? 'quotation' : 'quotations'} · {g.supplier_count}{' '}
              {g.supplier_count === 1 ? 'supplier' : 'suppliers'}
            </span>
          }
        >
          <p className="small">
            Line totals {g.line_total_range ? `${money(g.line_total_range[0])} – ${money(g.line_total_range[1])} ${g.currency}` : '—'}
            {' · '}
            Unit prices{' '}
            {g.unit_price_range ? `${money(g.unit_price_range[0])} – ${money(g.unit_price_range[1])} ${g.currency} (from ${g.unit_price_count} line${g.unit_price_count === 1 ? '' : 's'} with a quantity)` : 'not computable'}
            {g.sample_count === 1 && <span className="muted"> · one sample is not a market price</span>}
          </p>
          {g.conversion && (
            <p className="small muted">
              EUR shown with a manual rate: 1 {g.currency} = {g.conversion.eur_per_unit} EUR, as of {g.conversion.as_of} ({g.conversion.source}).
            </p>
          )}
          <div className="table-wrap">
            <table className="table compact">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Date</th>
                  <th>Line as printed</th>
                  <th className="num">Qty</th>
                  <th className="num">Line total</th>
                  <th className="num">Unit (printed)</th>
                  <th className="num">Unit (derived)</th>
                  <th>Scope</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {g.samples.map((s) => (
                  <tr key={s.line_id}>
                    <td>{s.supplier ?? <span className="unknown">unknown</span>}</td>
                    <td className="num">{s.issue_date ?? '—'}</td>
                    <td className="clip">
                      {s.label}
                      {s.is_bundle && <div className="muted small">bundle: {s.bundle_components.join(' + ') || 'scope not separable'}</div>}
                      {s.is_optional && <Badge>optional</Badge>}
                    </td>
                    <td className="num">{s.quantity ? `${Number(s.quantity)}${s.unit ? ` ${s.unit}` : ''}` : '—'}</td>
                    <td className="num">
                      {money(s.line_total)}
                      {s.line_total_eur && <div className="muted small">≈ {money(s.line_total_eur)} EUR</div>}
                    </td>
                    <td className="num">{money(s.printed_unit_price)}</td>
                    <td className="num">{money(s.derived_unit_price)}</td>
                    <td className="small">
                      {[
                        s.revisions_included != null ? `${s.revisions_included} revisions` : null,
                        s.resolution,
                        s.duration,
                        s.tax_treatment === 'excl_tax' ? 'excl. tax' : s.tax_treatment === 'incl_tax' ? 'incl. tax' : s.tax_treatment === 'no_tax' ? 'no tax' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td className="small">
                      <Link to={`/pricing/quotations/${s.quotation_id}?line=${s.line_id}`}>p. {s.page ?? '?'}</Link>
                      <div className="muted">{EVIDENCE_LABEL[s.evidence_type]}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ))}
    </>
  );
}

function PricingSettingsTab() {
  const { user, settings, reloadSettings } = useSession();
  const current = settings?.values.pricing;
  const [value, setValue] = useState<PricingSettings | null>(current ?? null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  useEffect(() => {
    if (current) setValue(current);
  }, [current]);
  if (!value) return <Loading what="Loading settings" />;
  const owner = user?.role === 'owner';
  const setRate = (i: number, patch: Partial<ExchangeRate>) =>
    setValue({ ...value, exchange_rates: value.exchange_rates.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  async function save() {
    setError('');
    setSaved('');
    try {
      await api('/api/settings/pricing', { method: 'PUT', body: { value } });
      await reloadSettings();
      setSaved('Saved.');
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <Section title="Pricing settings">
      {!owner && <p className="msg">Only the owner can change these settings.</p>}
      <fieldset disabled={!owner} className="plain">
        <label className="check block">
          <input type="checkbox" checked={value.editor_access} onChange={(e) => setValue({ ...value, editor_access: e.target.checked })} /> Editors may open quotations
          <span className="muted small"> (off: quotations are visible to the owner only)</span>
        </label>
        <div className="form-grid">
          <label className="field">
            <span>OCR languages (Tesseract codes, joined by +)</span>
            <input value={value.ocr_languages} onChange={(e) => setValue({ ...value, ocr_languages: e.target.value })} />
          </label>
          <label className="field">
            <span>Maximum pages per PDF</span>
            <input type="number" min={1} max={500} value={value.max_pages} onChange={(e) => setValue({ ...value, max_pages: Number(e.target.value) })} />
          </label>
        </div>
        <h3 className="sub">Manual exchange rates</h3>
        <p className="small muted">Amounts stay in their own currency. A conversion to EUR is shown only where a rate below exists, with its date and source.</p>
        {value.exchange_rates.length === 0 && <p className="muted small">No rate: no conversion is shown.</p>}
        {value.exchange_rates.map((r, i) => (
          <div className="rate-row" key={i}>
            <input aria-label="Currency" placeholder="USD" maxLength={3} value={r.currency} onChange={(e) => setRate(i, { currency: e.target.value.toUpperCase() })} />
            <input aria-label="EUR per unit" placeholder="0.92" value={r.eur_per_unit} onChange={(e) => setRate(i, { eur_per_unit: e.target.value })} />
            <input aria-label="As of" type="date" value={r.as_of} onChange={(e) => setRate(i, { as_of: e.target.value })} />
            <input aria-label="Source" placeholder="ECB reference rate" value={r.source} onChange={(e) => setRate(i, { source: e.target.value })} />
            <button type="button" className="btn ghost small" onClick={() => setValue({ ...value, exchange_rates: value.exchange_rates.filter((_, j) => j !== i) })}>
              Remove
            </button>
          </div>
        ))}
        <p>
          <button
            type="button"
            className="btn small"
            onClick={() => setValue({ ...value, exchange_rates: [...value.exchange_rates, { currency: '', eur_per_unit: '', as_of: new Date().toISOString().slice(0, 10), source: '' }] })}
          >
            Add a rate
          </button>
        </p>
        <p className="row gap">
          <button type="button" className="btn primary" onClick={() => void save()}>
            Save pricing settings
          </button>
          {saved && <span className="ok-text small">{saved}</span>}
        </p>
      </fieldset>
      <ErrorMsg>{error}</ErrorMsg>
    </Section>
  );
}
